import { api } from '../../lib/api';
import { albums, type Album, type AlbumDetail } from '../albums/api';
import type { TrackSummary } from '../tracks/TrackList';

/**
 * The library endpoints: favourites, and playlists.
 *
 * ## Playlists are collections, and almost nothing here is new
 *
 * `collections` already held a `playlist` type and already had create, update,
 * delete, add-track, reorder and remove-track - the table was built for this.
 * So this is mostly the album API with the type pinned, rather than a parallel
 * set of endpoints doing the same work under another name.
 *
 * The one thing that genuinely differs is *what may go in one*. An album is a
 * release its owner made and takes only their tracks; a playlist is a selection
 * of the platform's music and takes anything the owner can already play. That
 * rule lives in `CollectionAccess::playlistTrack` on the server, because it is
 * the kind of rule a client must not be trusted with.
 */

/**
 * A playlist in a list. `owned` is present on the library route and says
 * whether this one is the reader's or one they follow; absent on a profile,
 * where every row belongs to the profile.
 */
export type Playlist = Album & {
  owned?: boolean;
  /** Somebody else's playlist that this person helps keep. */
  shared?: boolean;
};

/** Somebody helping keep a playlist. `invited` until they have answered. */
export interface Collaborator {
  id: number;
  username: string;
  display_name: string;
  verified: boolean;
  avatar: string | null;
  status: 'invited' | 'active';
}

export type PlaylistDetail = AlbumDetail & {
  /** A public playlist somebody else keeps, which the reader may follow. */
  followable: boolean;
  followed: boolean;
  follower_count: number;
  /**
   * Whether the reader may change the contents: the owner, or somebody
   * they have asked to help keep it. Not the title, the cover, who can see
   * it or whether it exists - those stay `is_owner`.
   */
  can_edit: boolean;
  /** Everybody on it. The invited are shown to the owner alone. */
  collaborators: Collaborator[];
  /** The reader's own part in it, if any. */
  my_collaboration: 'invited' | 'active' | null;
};

export interface PlaylistDraft {
  title: string;
  visibility?: Playlist['visibility'];
}

export const playlists = {
  /**
   * Somebody's playlists.
   *
   * The owner sees their private and unlisted ones; a visitor sees the public
   * ones - the same rule the albums tab has always used, which is why this is
   * the albums route with a type on it rather than a route of its own.
   */
  mine: (username: string) =>
    api.get<{ items: Playlist[] }>(`/users/${username}/collections?type=playlist`),

  /**
   * The library: made, then followed, each row saying which. This is what
   * the rail, the dock and the room panel draw - a followed playlist is
   * playable from all three and editable from none of them.
   */
  library: () => api.get<{ items: Playlist[] }>('/me/playlists'),

  /** Who helps keep it. */
  collaborators: (id: number) =>
    api.get<{ collaborators: Collaborator[] }>(`/collections/${id}/collaborators`),
  /** Ask somebody. They answer; nothing changes until they do. */
  invite: (id: number, username: string) =>
    api.post<{ invited: boolean; collaborators: Collaborator[] }>(
      `/collections/${id}/collaborators`,
      { username },
    ),
  acceptInvite: (id: number) => api.post<{ joined: boolean }>(`/collections/${id}/collaborators/accept`, {}),
  /** Declining, leaving and being removed are one act with three names. */
  removeCollaborator: (id: number, userId?: number) =>
    api.delete<{ removed: boolean }>(
      userId === undefined ? `/collections/${id}/collaborators` : `/collections/${id}/collaborators/${userId}`,
    ),

  follow: (id: number) =>
    api.post<{ followed: boolean; follower_count: number }>(`/collections/${id}/follow`, {}),
  unfollow: (id: number) =>
    api.delete<{ followed: boolean; follower_count: number }>(`/collections/${id}/follow`),

  get: (id: number | string) => api.get<PlaylistDetail>(`/collections/${id}`),

  /*
   * Private unless said otherwise.
   *
   * A playlist made in two seconds from the player bar is a personal list, and
   * publishing somebody's listening by default is not a thing to get wrong
   * once. The create form offers the choice; this is only what it starts on.
   */
  create: (draft: PlaylistDraft) =>
    api.post<Playlist>('/collections', {
      type: 'playlist',
      visibility: 'private',
      ...draft,
    }),

  /** The playlist's own title. Never a track's - those belong to their artists. */
  rename: (id: number, title: string) => albums.update(id, { title }),

  setVisibility: (id: number, visibility: Playlist['visibility']) =>
    albums.update(id, { visibility }),

  remove: (id: number) => albums.remove(id),

  addTrack: (id: number, trackId: number) => albums.addTrack(id, trackId),

  removeTrack: (id: number, trackId: number) => albums.removeTrack(id, trackId),

  reorder: (id: number, trackIds: number[]) => albums.reorder(id, trackIds),
};

/**
 * Favourited tracks.
 *
 * Same key as `FavoritesPage` uses, so the library panel and the page cannot
 * disagree about what is in it, and opening the panel after visiting the page
 * costs no request.
 */
export const FAVORITES_KEY = ['favorites'];

export const favorites = () => api.get<{ items: TrackSummary[] }>('/me/favorites');
